import Konva from 'konva';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useAppSelector } from '../../../app/hooks';
import useIsBlueberryFeature from '../../scanDataApi/hooks/useIsBlueberryFeature';
import useScanData from '../../scanDataApi/hooks/useScanData';
import { ScanData } from '../../scanDataApi/scanDataDefinitions';
import { selectCurrPointId } from '../../select/selectSlice';
import { downloadURI } from '../../utils/downloadURI';
import { selectImageResolution } from '../imageSlice';

type ImageContextType = {
  stageRef?: Konva.Stage | null;
  imageContainerWidth?: number;
  setImageContainerWidth?: (val: number) => void;
  setStageRef?: (ref: Konva.Stage | null) => void;
  getStageRef?: () => Konva.Stage | null;
  downloadImageFromStage: (fileName: string) => void;
  currPoint: ScanData | undefined;
  currPointLoading: boolean;
  currPointError: Error | null;
};

type Props = {
  children?: React.ReactNode;
};

const ImageContext = createContext<ImageContextType>({} as ImageContextType);
ImageContext.displayName = 'ImageContext';

export const ImageProvider: React.FC<Props> = React.memo(({ children }) => {
  const imageResolution = useAppSelector(selectImageResolution);
  const [imageContainerWidth, setImageContainerWidth] = useState<number>(480);
  const [stageRef, setStageRef] = useState<Konva.Stage | null>(null);
  const { scanData, blueberryData } = useScanData();
  const isBlueberry = useIsBlueberryFeature();
  const currPointId = useAppSelector(selectCurrPointId);
  const getStageRef = useCallback(() => stageRef, [stageRef]);

  const currentData = useMemo(() => {
    return isBlueberry ? blueberryData : scanData;
  }, [scanData, blueberryData, isBlueberry]);

  // get the current point from the raw point data
  const currPoint = useMemo(() => {
    if (!currentData.data) return undefined;
    return currentData.data.find((point) => point.point_id === currPointId);
  }, [currPointId, currentData]);

  const downloadImageFromStage = useCallback(
    (fileName: string) => {
      if (!stageRef) return;
      const uri = stageRef.toDataURL({
        pixelRatio: imageResolution / imageContainerWidth,
      });
      downloadURI(uri, fileName);
    },
    [stageRef, imageResolution, imageContainerWidth]
  );

  const value = useMemo(() => {
    return {
      stageRef,
      setStageRef,
      getStageRef,
      downloadImageFromStage,
      imageContainerWidth,
      setImageContainerWidth,
      currPoint,
      currPointLoading: currentData.isLoading,
      currPointError: currentData.error,
    };
  }, [
    stageRef,
    getStageRef,
    downloadImageFromStage,
    imageContainerWidth,
    currPoint,
    currentData.isLoading,
    currentData.error,
  ]);

  return (
    <ImageContext.Provider value={value}>{children}</ImageContext.Provider>
  );
});

export const useImageContext = () => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImageContext must be used within a ImageContext');
  }
  return context;
};
